export type AppClientPermissionCustomizationFlags = {
  products_customized?: boolean;
  meta_audience_categories_customized?: boolean;
  meta_audiences_customized?: boolean;
  tableau_workbooks_customized?: boolean;
};

/** Defaults for new client-user rows before the API assigns explicit overrides. */
export const defaultAppClientPermissionCustomizationFlags = {
  products_customized: false,
  meta_audience_categories_customized: false,
  meta_audiences_customized: false,
  tableau_workbooks_customized: false,
} as const satisfies AppClientPermissionCustomizationFlags;

export function isLiveAppClientUser(user: {
  deleted_at?: string | null;
}): boolean {
  return user.deleted_at == null;
}

type AudiencePermissionSelections = {
  meta_audience_categories?: { id?: number }[] | null;
  meta_audiences?: { id?: number }[] | null;
};

/** True when at least one audience category or individual audience is selected. */
export function clientHasAudienceSelections(
  selections: AudiencePermissionSelections,
): boolean {
  const categoryCount = selections.meta_audience_categories?.length ?? 0;
  const audienceCount = selections.meta_audiences?.length ?? 0;
  return categoryCount + audienceCount > 0;
}

/**
 * Bulk edit, custom overrides, and client audience config: require at least one
 * selection when the client has audience permissions configured; otherwise pass
 * (trial / product-only clients with no audience dimensions).
 */
export function audienceCustomOrBulkSelectionsValid(
  client: AudiencePermissionSelections,
  selections: AudiencePermissionSelections,
): boolean {
  const clientCategoryCount = client.meta_audience_categories?.length ?? 0;
  const clientAudienceCount = client.meta_audiences?.length ?? 0;
  if (clientCategoryCount + clientAudienceCount === 0) {
    return true;
  }
  return clientHasAudienceSelections(selections);
}

/**
 * True when inherit-mode selections cover each non-empty client dimension
 * (categories and individual audiences). Used when the user inherits client
 * permissions, not for custom overrides (those use `clientHasAudienceSelections`).
 */
export function audiencePermissionSelectionsValid(
  client: AudiencePermissionSelections,
  selections: AudiencePermissionSelections,
): boolean {
  const clientCategoryCount = client.meta_audience_categories?.length ?? 0;
  const clientAudienceCount = client.meta_audiences?.length ?? 0;
  const categoryCount = selections.meta_audience_categories?.length ?? 0;
  const audienceCount = selections.meta_audiences?.length ?? 0;
  return (
    (clientCategoryCount === 0 || categoryCount > 0) &&
    (clientAudienceCount === 0 || audienceCount > 0)
  );
}

function idSetsDiffer(userIds: Set<number>, clientIds: Set<number>): boolean {
  if (userIds.size === 0) return false;
  if (userIds.size !== clientIds.size) return true;
  for (const id of userIds) {
    if (!clientIds.has(id)) return true;
  }
  return false;
}

/** True when resolved permission ID sets differ (empty user set vs non-empty client = customized). */
export function appClientPermissionIdSetsDiffer(
  userItems: { id: number }[] | undefined | null,
  clientItems: { id: number }[] | undefined | null,
): boolean {
  const userIds = new Set((userItems ?? []).map((item) => item.id));
  const clientIds = new Set((clientItems ?? []).map((item) => item.id));
  if (userIds.size !== clientIds.size) return true;
  for (const id of userIds) {
    if (!clientIds.has(id)) return true;
  }
  return false;
}

function hasAnyCustomizationFlag(
  user: AppClientPermissionCustomizationFlags | null | undefined,
): boolean {
  return Boolean(
    user?.products_customized ||
    user?.meta_audience_categories_customized ||
    user?.meta_audiences_customized ||
    user?.tableau_workbooks_customized,
  );
}

/**
 * True when any per-user permission dimension is explicitly customized. Takes a
 * resolved `UserProfile`, whose permission lists are fully populated — for a row
 * from the client payload use `appClientUserRowHasCustomPermissions` instead.
 */
export function userHasCustomAppClientPermissions(
  user: AppClientPermissionCustomizationFlags | null | undefined,
  userProducts: { id: number }[] | undefined | null,
  clientProducts: { id: number }[] | undefined | null,
  userCategories: { id: number }[] | undefined | null,
  clientCategories: { id: number }[] | undefined | null,
  userAudiences: { id?: number }[] | undefined | null,
  clientAudiences: { id?: number }[] | undefined | null,
): boolean {
  if (hasAnyCustomizationFlag(user)) return true;

  return (
    userClientProductSetsDiffer(userProducts, clientProducts) ||
    userClientMetaAudienceCategorySetsDiffer(
      userCategories,
      clientCategories,
    ) ||
    userClientMetaAudienceSetsDiffer(userAudiences, clientAudiences)
  );
}

/**
 * `userHasCustomAppClientPermissions` for a user row off `GET /app/clients/{id}`.
 *
 * That payload deliberately returns an empty `meta_audiences` on every nested user
 * (the resolved list comes from `GET /app/clients/{id}/users/{user_id}`), so the
 * audience dimension is carried solely by `meta_audiences_customized`. The API
 * keeps that flag in step with the presence of per-user audience rows, so a set
 * diff would add nothing here — and comparing an always-empty list against a
 * populated client list must not be read as either "inherits" or "customized".
 */
export function appClientUserRowHasCustomPermissions(
  row:
    | (AppClientPermissionCustomizationFlags & {
        products?: { id: number }[] | null;
        meta_audience_categories?: { id: number }[] | null;
      })
    | null
    | undefined,
  clientProducts: { id: number }[] | undefined | null,
  clientCategories: { id: number }[] | undefined | null,
): boolean {
  if (hasAnyCustomizationFlag(row)) return true;

  return (
    userClientProductSetsDiffer(row?.products, clientProducts) ||
    userClientMetaAudienceCategorySetsDiffer(
      row?.meta_audience_categories,
      clientCategories,
    )
  );
}

export function clearAppClientPermissionCustomizationFlags(
  user: AppClientPermissionCustomizationFlags,
): void {
  user.products_customized = false;
  user.meta_audience_categories_customized = false;
  user.meta_audiences_customized = false;
  user.tableau_workbooks_customized = false;
}

/** True when the user's enabled product IDs differ from the client's (including empty vs non-empty). */
export function userClientProductSetsDiffer(
  userProducts: { id: number }[] | undefined | null,
  clientProducts: { id: number }[] | undefined | null,
): boolean {
  const userIds = new Set((userProducts ?? []).map((p) => p.id));
  const clientIds = new Set((clientProducts ?? []).map((p) => p.id));
  return idSetsDiffer(userIds, clientIds);
}

/** True when the user's meta audience category IDs differ from the client's (empty user set = inherit). */
export function userClientMetaAudienceCategorySetsDiffer(
  userCategories: { id: number }[] | undefined | null,
  clientCategories: { id: number }[] | undefined | null,
): boolean {
  const userIds = new Set((userCategories ?? []).map((c) => c.id));
  const clientIds = new Set((clientCategories ?? []).map((c) => c.id));
  return idSetsDiffer(userIds, clientIds);
}

/** True when the user's meta audience IDs differ from the client's (empty user set = inherit). */
export function userClientMetaAudienceSetsDiffer(
  userAudiences: { id?: number }[] | undefined | null,
  clientAudiences: { id?: number }[] | undefined | null,
): boolean {
  const userIds = new Set(
    (userAudiences ?? [])
      .map((a) => a.id)
      .filter((id): id is number => typeof id === "number"),
  );
  const clientIds = new Set(
    (clientAudiences ?? [])
      .map((a) => a.id)
      .filter((id): id is number => typeof id === "number"),
  );
  return idSetsDiffer(userIds, clientIds);
}
